import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import productService from '../services/productService';
import { formatPrice, getPrimaryImage, handleImageError } from '../utils/apiHelpers';
import { useSearch } from '../contexts/SearchContext';

function SearchOverlay() {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const inputRef = useRef(null);
  const navigate = useNavigate();
  const { isSearchOpen, closeSearch } = useSearch();

  // Handle close overlay
  const handleClose = (e) => {
    e.preventDefault();
    closeSearch();
    // Clear search after animation
    setTimeout(() => {
      setSearchQuery('');
      setSearchResults([]);
      setHasSearched(false);
    }, 300);
  };

  // Auto-focus input when overlay opens
  useEffect(() => {
    if (isSearchOpen && inputRef.current) {
      setTimeout(() => inputRef.current.focus(), 100);
    }
  }, [isSearchOpen]);

  // Search products
  const handleSearch = async (e) => {
    e.preventDefault();

    const trimmedQuery = searchQuery.trim();

    if (trimmedQuery.length === 0) {
      return;
    }

    if (trimmedQuery.length < 2) {
      return;
    }

    setLoading(true);
    setHasSearched(true);

    try {
      const response = await productService.getProducts({
        search: trimmedQuery,
        page: 0,
        size: 6 // Limit to 6 results for overlay
      });

      if (response.success) {
        setSearchResults(response.products || []);
      } else {
        setSearchResults([]);
      }
    } catch (error) {
      console.error('Search error:', error);
      setSearchResults([]);
    } finally {
      setLoading(false);
    }
  };

  // Navigate to full search results
  const viewAllResults = () => {
    if (searchQuery.trim()) {
      closeSearch();
      navigate(`/product-listing?search=${encodeURIComponent(searchQuery.trim())}`);
      // Clear search after navigation
      setTimeout(() => {
        setSearchQuery('');
        setSearchResults([]);
        setHasSearched(false);
      }, 300);
    }
  };

  return (
    <>
      {/* START SEARCH AREA */}
      <div className={`search-box-overlay ${isSearchOpen ? 'fixed-box' : ''}`}>
        <a href="/" onClick={handleClose}>
          <i className="fas fa-times cross-sign" id="close-window"></i>
        </a>
        <div className="container">
          <div className="row">
            <div className="col-12 search-col">
              <form onSubmit={handleSearch}>
                <div className="input-group search-box-form">
                  <input
                    ref={inputRef}
                    type="text"
                    className="form-control"
                    placeholder="Search Here"
                    aria-label="Search Here"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                  <div className="input-group-prepend">
                    <button className="input-group-text" type="submit" id="basic-addon1">
                      {loading ? (
                        <i className="fas fa-spinner fa-spin"></i>
                      ) : (
                        <i className="fas fa-search"></i>
                      )}
                    </button>
                  </div>
                </div>
              </form>
            </div>

            {/* Search Results */}
            {hasSearched && (
              <div className="search-listing row">
                <div className="col-12 mb-4">
                  <div className="d-flex justify-content-between align-items-center">
                    <h4>
                      {loading ? 'Searching...' : `${searchResults.length} Result${searchResults.length !== 1 ? 's' : ''}`}
                    </h4>
                    {searchResults.length > 0 && (
                      <button
                        className="btn btn-sm pink-gradient-btn-into-black"
                        onClick={viewAllResults}
                      >
                        View All
                      </button>
                    )}
                  </div>
                </div>

                <div className="col-12">
                  {loading ? (
                    <div className="text-center py-4">
                      <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                    </div>
                  ) : searchResults.length > 0 ? (
                    <div className="listing-search-scroll">
                      {searchResults.map((product) => (
                        <div key={product.id} className="media row mb-3">
                          <div className="img-holder ml-1 mr-2 col-4">
                            <Link
                              to={`/product-detail/${product.id}`}
                              onClick={closeSearch}
                            >
                              <img
                                src={getPrimaryImage(product)}
                                className="align-self-center"
                                alt={product.name}
                                onError={handleImageError}
                              />
                            </Link>
                          </div>
                          <div className="media-body mt-auto mb-auto col-8">
                            <h5 className="name">
                              <Link
                                to={`/product-detail/${product.id}`}
                                onClick={closeSearch}
                              >
                                {product.name}
                              </Link>
                            </h5>
                            <p className="category">
                              {product.category?.name || 'Uncategorized'}
                            </p>
                            <p className="price mb-2">
                              <strong>{formatPrice(product.price)}</strong>
                              {product.compareAtPrice && (
                                <span className="text-muted text-decoration-line-through ms-2">
                                  {formatPrice(product.compareAtPrice)}
                                </span>
                              )}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-4">
                      <i className="fas fa-search mb-3" style={{ fontSize: '48px', color: '#ddd' }}></i>
                      <p className="text-muted">
                        No products found for "{searchQuery}"
                      </p>
                      <p className="text-muted small">
                        Try a different search term
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      {/* END SEARCH AREA */}
    </>
  );
}

export default SearchOverlay;
